import {
    StatsigApplePayVariants,
    STATSIG_APPLE_PAY_EXPERIMENT_KEY,
} from '../constants/statsig';
import { getEbDomain, isEventbriteTLD } from '../selectors/app';
import { getEventCountryCode } from '../selectors/event';
import {
    isApplePaySplitTreatmentEnabled,
    isLoginInterstitialSplitTreatmentEnabled,
} from '../selectors/splitio';
import { CheckoutState } from '../state';
import { isApplePaySupported } from '../utils/applePay';
import { isIosApp } from '../utils/browser';
import { getExperimentValueToOverride } from '../utils/statsig';

const QA_ENV_URLS_REGEX = /^(https?:\/\/)?(www.)?(evbqa(\d)*).com$/;
const DEV_ENV_URL_REGEX = /^(https?:\/\/)?dev.evbqa.com(:(\d)*)?\/?/;

const isQAEnv = (host: string) => QA_ENV_URLS_REGEX.test(host);
const isDevEnv = (host: string) => DEV_ENV_URL_REGEX.test(host);

export const isEventbriteTLDInDevOrQAEnv = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const parent = urlParams.get('parent') || '';
    return (
        (isQAEnv(window.location?.host) && isQAEnv(parent)) ||
        (isDevEnv(window.location?.host) && isDevEnv(parent))
    );
};

export const isTLDWithApplePayEnabled = (state: CheckoutState) =>
    (isEventbriteTLD(state) && getEbDomain(state) === 'eventbrite.com') ||
    isEventbriteTLDInDevOrQAEnv() ||
    isIosApp(state);

export const isStatsigFreeTrialEnabledForApplePay = (
    state: CheckoutState,
): boolean =>
    // TODO: EB-185440 Remove the Country Code check when paying with ApplePay in Quebec events is fixed
    getEventCountryCode(state) !== 'CA' &&
    isTLDWithApplePayEnabled(state) &&
    isApplePaySplitTreatmentEnabled(state) &&
    isApplePaySupported();

export const isStatsigFreeTrialEnabledForInterstitialLogin = (
    state: CheckoutState,
): boolean => {
    return (
        (isEventbriteTLD(state) || isEventbriteTLDInDevOrQAEnv()) &&
        isLoginInterstitialSplitTreatmentEnabled(state)
    );
};

export const isStatsigFreeTrialEnabled = (state: CheckoutState) =>
    isStatsigFreeTrialEnabledForInterstitialLogin(state) ||
    isStatsigFreeTrialEnabledForApplePay(state);

export const getExperimentValue = (
    state: CheckoutState,
    experimentName: string,
) => {
    const valueToOverride = getExperimentValueToOverride(state, experimentName);

    if (valueToOverride) {
        return valueToOverride;
    }
    return state.statsig?.experiments?.[experimentName]?.value;
};

export const isInVariantWithApplePayAsFirstPaymentMethod = (
    state: CheckoutState,
): boolean =>
    getExperimentValue(state, STATSIG_APPLE_PAY_EXPERIMENT_KEY) ===
    StatsigApplePayVariants.PreselectedAndFirst;

export const isApplePayAvailableInExperiment = (
    state: CheckoutState,
): boolean =>
    [
        StatsigApplePayVariants.NotPreselectedAndLast,
        StatsigApplePayVariants.PreselectedAndFirst,
    ].includes(
        getExperimentValue(
            state,
            STATSIG_APPLE_PAY_EXPERIMENT_KEY,
        ) as StatsigApplePayVariants,
    );
